/**
 * ScenicRouteAnimationOverlay - Unified Cinematic Animation Overlay
 * Powers both 🔄 Round About ("Have some fun on the Round About!") and
 * 🚧 Detour ("Take the scenic route on this Detour!") using the GPS "Recalculating!" concept.
 */

let activeScenicRouteInstance = null;

export class ScenicRouteAnimationOverlay {
  /**
   * Displays the Scenic Route / Rotary GPS animation overlay.
   *
   * @param {Object} [options]
   * @param {'roundabout'|'detour'} [options.type='roundabout'] - Animation variant type
   * @param {string} [options.playerName='Driver'] - Name of player navigating
   * @param {string} [options.who='p1'] - Seat ID
   * @param {number} [options.hole=35] - Hole number where hazard triggered
   * @param {number} [options.progress=0] - Hazard loop progress (e.g. 0 to 7)
   * @param {Object} [options.card] - Card object { r, s } representing player's card
   * @param {Object} [options.settings] - Game settings object
   * @param {Function} [options.onComplete] - Callback on dismiss or completion
   * @returns {ScenicRouteAnimationOverlay|null}
   */
  static show(options = {}) {
    const settings = options.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (settings) {
      const specificOpt = options.type === 'detour' ? settings.detourAnimation : settings.roundAboutAnimation;
      const optVal = specificOpt !== undefined ? specificOpt : settings.scenicRouteAnimation;
      if (optVal === 'no' || optVal === 'off' || optVal === false) {
        if (typeof options.onComplete === 'function') options.onComplete();
        return null;
      }
    }

    if (typeof document === 'undefined') {
      if (typeof options.onComplete === 'function') options.onComplete();
      return null;
    }

    if (activeScenicRouteInstance) {
      activeScenicRouteInstance.close(false);
    }

    const instance = new ScenicRouteAnimationOverlay(options);
    activeScenicRouteInstance = instance;
    instance.mount();
    return instance;
  }

  static hide() {
    if (activeScenicRouteInstance) {
      activeScenicRouteInstance.close(true);
    }
  }

  constructor(options = {}) {
    this.options = options;
    this.type = options.type === 'detour' ? 'detour' : 'roundabout';
    this.onComplete = options.onComplete || (() => {});
    this.overlayEl = null;
    this.autoCloseTimer = null;
    this.removeTimer = null;
    this.stepTimers = [];
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  formatCard(card, fallbackRank = 'J', fallbackSuit = '♠') {
    if (!card) {
      return { r: fallbackRank, s: fallbackSuit, isRed: fallbackSuit === '♥' || fallbackSuit === '♦' };
    }
    if (Array.isArray(card) && card.length > 0) {
      card = card[card.length - 1];
    }
    if (typeof card === 'string') {
      const match = card.match(/^([0-9AJQK]+)([HDSC♥♦♠♣]?)$/i);
      if (match) {
        card = { r: match[1], s: match[2] };
      }
    }
    let r = card.r !== undefined ? card.r : (card.rank !== undefined ? card.rank : fallbackRank);
    if (r === 1 || r === '1') r = 'A';
    else if (r === 11 || r === '11') r = 'J';
    else if (r === 12 || r === '12') r = 'Q';
    else if (r === 13 || r === '13') r = 'K';
    r = String(r).toUpperCase();

    let s = card.s || card.suit || fallbackSuit;
    if (s === 'H' || s === 'h') s = '♥';
    else if (s === 'D' || s === 'd') s = '♦';
    else if (s === 'S' || s === 's') s = '♠';
    else if (s === 'C' || s === 'c') s = '♣';

    const isRed = s === '♥' || s === '♦';
    return { r, s, isRed };
  }

  buildHtml() {
    const pName = this.options.playerName || 'Driver';
    const hole = this.options.hole !== undefined ? this.options.hole : 35;
    const card = this.formatCard(this.options.card, 'Q', '♥');
    const isDetour = this.type === 'detour';

    const bannerPill = isDetour ? 'ROAD WORK AHEAD · GPS DETOUR' : 'TRAFFIC ROTARY · ROUND ABOUT';
    const titleText = isDetour
      ? `🚧 Detour: ${pName} Taking the Scenic Route!`
      : `🔄 Round About: ${pName} Having Fun on the Rotary!`;
    const subtitleText = isDetour
      ? `Main Road Closed at Hole ${hole} · 7-Hole Scenic Byway Activated`
      : `Approaching Hole ${hole} Rotary · 7-Hole Rotary Loop In Progress`;

    const gpsTurnText = isDetour
      ? 'In 50 ft, take bumpy scenic dirt road'
      : 'At roundabout, take the 7th exit';

    return `
      <!-- Ambient GPS Strobe Effect -->
      <div class="ambient-gps-strobe" aria-hidden="true"></div>

      <!-- Skip Button -->
      <button type="button" class="scenic-skip-btn" id="scenicRouteSkipBtn" aria-label="Skip Animation">
        Skip ✕
      </button>

      <!-- Top Header & Banner -->
      <div class="scenic-route-banner">
        <div class="badge-pill-wrap">
          <span class="pulsing-dot"></span>
          <span>${bannerPill}</span>
          <span class="pulsing-dot"></span>
        </div>
        <h2 class="scenic-title">${titleText}</h2>
        <div class="scenic-subtitle">${subtitleText}</div>
      </div>

      <!-- In-Car Dashboard GPS HUD Bar -->
      <div class="gps-dash-hud">
        <div class="speedometer-cluster">
          <span class="speed-val" id="dashSpeedometer">45</span>
          <span class="speed-unit">MPH</span>
        </div>
        <div class="gps-route-instruction">
          <span class="turn-icon">${isDetour ? '🚧' : '🔄'}</span>
          <span id="gpsTurnLabel">${gpsTurnText}</span>
        </div>
        <div class="gps-recalculating-pill" id="gpsRecalcBadge">
          <span>⚠️ RECALCULATING...</span>
        </div>
      </div>

      <!-- Main Animation Stage -->
      <div class="scenic-route-stage" id="scenicStage">
        <!-- Paved Highway Base Floor -->
        <div class="stage-highway">
          <div class="dashed-line"></div>
        </div>

        ${isDetour ? `
          <!-- Detour Construction Zone Arena -->
          <div class="detour-arena">
            <div class="bumpy-dirt-road">
              <div class="road-rock r1"></div>
              <div class="road-rock r2"></div>
              <div class="road-rock r3"></div>
            </div>
            <div class="construction-sawhorse">
              <span class="amber-beacon left"></span>
              <span class="sawhorse-sign">ROAD CLOSED</span>
              <span class="amber-beacon right"></span>
            </div>
            <div class="orange-detour-sign">
              <div class="sign-main">DETOUR</div>
              <div class="sign-arrow">➔</div>
            </div>
            <div class="roadside-cow" title="Roadside onlooker">🐮</div>
          </div>
        ` : `
          <!-- Round About Rotary Loop Arena -->
          <div class="roundabout-arena">
            <div class="rotary-circle-ring"></div>
            <div class="rotary-center-island">
              <span>⛲</span>
              <span class="rotary-label">ROTARY</span>
            </div>
            <div class="rotary-orbit-track" id="rotaryOrbitTrack"></div>
          </div>
        `}

        <!-- The Player's Vehicle & Driver Card -->
        <div class="player-car-wrap" id="playerCar">
          <div class="driver-card ${card.isRed ? 'red' : 'black'}">
            <div class="corner tl">
              <span class="r">${card.r}</span>
              <span class="s">${card.s}</span>
            </div>
            <div class="card-face-center">
              <span class="normal-pip">${card.s}</span>
              <span class="dizzy-face">😵‍💫</span>
            </div>
            <div class="corner br">
              <span class="r">${card.r}</span>
              <span class="s">${card.s}</span>
            </div>
            <div class="steering-wheel"></div>
          </div>

          <div class="car-chassis">
            <div class="headlight"></div>
          </div>
          <div class="car-wheel front"></div>
          <div class="car-wheel rear"></div>
          <div class="road-dust-cloud"></div>
        </div>
      </div>
    `;
  }

  mount() {
    let el = document.getElementById('scenicRouteAnimationOverlay');
    if (!el) {
      el = document.createElement('div');
      el.id = 'scenicRouteAnimationOverlay';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', this.type === 'detour' ? 'Detour Animation' : 'Round About Animation');
      document.body.appendChild(el);
    }

    el.className = `scenic-route-overlay theme-${this.type}`;
    this.overlayEl = el;
    el.innerHTML = this.buildHtml();

    const skipBtn = el.querySelector('#scenicRouteSkipBtn');
    if (skipBtn) {
      skipBtn.addEventListener('click', () => this.close(true));
    }

    window.addEventListener('keydown', this.handleKeyDown);

    // Force layout flush before activating CSS transitions
    void el.offsetWidth;
    el.classList.add('active');

    this.startChoreography();
  }

  getAudio() {
    return (typeof window !== 'undefined' && (window.audio || window.audioManager || window.sfx || (window.CC && (window.CC.audio || window.CC.audioManager || window.CC.sfx)))) || null;
  }

  startChoreography() {
    const audio = this.getAudio();

    // 0.0s: GPS navigation recalculation chime & static warble
    if (audio && typeof audio.gpsChimeRecalculate === 'function') {
      audio.gpsChimeRecalculate();
    }

    // 0.65s: Divert into hazard (swerving into detour or spinning on roundabout)
    this.scheduleStep(650, () => {
      const car = this.overlayEl?.querySelector('#playerCar');
      const speedEl = this.overlayEl?.querySelector('#dashSpeedometer');
      const labelEl = this.overlayEl?.querySelector('#gpsTurnLabel');

      if (this.type === 'detour') {
        if (car) car.classList.add('detour-swerved');
        if (speedEl) speedEl.textContent = '22';
        if (labelEl) labelEl.textContent = 'BUMPY SCENIC ROUTE AHEAD!';
        if (audio && typeof audio.bumpyDetourRattle === 'function') {
          audio.bumpyDetourRattle();
        }
      } else {
        const orbitTrack = this.overlayEl?.querySelector('#rotaryOrbitTrack');
        if (orbitTrack && car) {
          orbitTrack.appendChild(car);
          orbitTrack.classList.add('spinning');
          car.style.left = '50%';
          car.style.top = '-15px';
          car.style.transform = 'translate(-50%, 0) scale(0.9)';
        }
        if (speedEl) speedEl.textContent = '15';
        if (labelEl) labelEl.textContent = 'ROUNDABOUT EXIT MISSED! WHEEEE!';
        if (audio && typeof audio.rotarySpin === 'function') {
          audio.rotarySpin();
        }
      }
    });

    // 1.8s: Punchline audio & second wave (rattle or spin)
    this.scheduleStep(1800, () => {
      const speedEl = this.overlayEl?.querySelector('#dashSpeedometer');
      if (this.type === 'detour') {
        if (speedEl) speedEl.textContent = '28';
      } else {
        if (speedEl) speedEl.textContent = '35';
      }
    });

    // 4.25s: Auto-close overlay gracefully
    this.autoCloseTimer = setTimeout(() => {
      this.close(true);
    }, 4250);
  }

  scheduleStep(delay, fn) {
    const t = setTimeout(fn, delay);
    this.stepTimers.push(t);
  }

  handleKeyDown(e) {
    if (e.key === 'Escape' || e.key === 'Esc' || e.key === 'Enter' || e.key === ' ') {
      this.close(true);
    }
  }

  close(triggerCallback = true) {
    window.removeEventListener('keydown', this.handleKeyDown);

    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
    if (this.removeTimer) {
      clearTimeout(this.removeTimer);
      this.removeTimer = null;
    }
    this.stepTimers.forEach(t => clearTimeout(t));
    this.stepTimers = [];

    const el = this.overlayEl;
    this.overlayEl = null;

    if (activeScenicRouteInstance === this) {
      activeScenicRouteInstance = null;
    }

    if (el) {
      el.classList.remove('active');
      if (triggerCallback) {
        this.removeTimer = setTimeout(() => {
          if (el && el.parentNode) {
            el.parentNode.removeChild(el);
          }
        }, 300);
      } else {
        if (el && el.parentNode) {
          el.parentNode.removeChild(el);
        }
      }
    }

    if (triggerCallback && typeof this.onComplete === 'function') {
      const cb = this.onComplete;
      this.onComplete = null;
      try {
        cb();
      } catch (err) {
        console.error('[ScenicRouteAnimationOverlay] onComplete error:', err);
      }
    }
  }
}
